<template>
  <div class="brail" :class="{ 'is-open': open }">
    <button type="button" class="brail__head" :aria-expanded="open" @click="toggle">
      <el-icon class="brail__caret"><ArrowRight /></el-icon>
      <span class="brail__label">기한 없는 업무</span>
      <span class="count-badge u-num">{{ items.length }}</span>
    </button>

    <div v-if="open" class="brail__viewport">
      <div class="brail__strip">
        <button
          v-for="it in items"
          :key="it.uid"
          type="button"
          class="brail__card"
          :class="[`p-${it.priority || 'medium'}`, { 'is-done': it.status === 'done' }]"
          :title="it.title"
          @click="$emit('item-click', it)"
        >
          <!-- 상단 3px 우선순위 띠. TaskCard 와 같은 규칙(빨강=매우중요,
               앰버=중요, 초록=일반)이라 칸반에서 보던 카드와 같은 것으로 읽힌다. -->
          <span class="brail__prio" />

          <span class="brail__body">
            <span class="brail__title u-clamp-2">{{ it.title }}</span>

            <span class="brail__foot">
              <!-- 상태색 점. AgendaItem 의 color 는 업무일 때 **상태색**이다
                   (우선순위색이 아니다 — 그건 위의 띠가 맡는다). -->
              <span class="brail__dot" :style="{ background: it.color }" aria-hidden="true" />
              <span class="brail__status">{{ it.statusLabel }}</span>

              <el-avatar
                v-if="it.owner"
                :size="20"
                :src="avatarOf(it.owner)"
                :title="it.owner.nickname"
                class="brail__av"
              >{{ initialOf(it.owner) }}</el-avatar>
              <span v-else class="brail__unassigned">미배정</span>
            </span>
          </span>

          <!--
            어포던스.
            이 카드는 캘린더로 **끌어다 놓을 수 없다** — 월 그리드의 드래그
            (useMonthDragMove)는 그리드 안 칩에서 시작하는 포인터만 받고,
            잡은 지점이 그리드 밖이면 아예 무장되지 않는다. 그래서 "끌 수 있어
            보이는데 안 되는" 카드가 되지 않도록, 실제로 되는 길을 적어 둔다:
            탭 → 업무 상세 → 기한 지정.
          -->
          <span class="brail__act">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" /><path d="M3.5 10h17M8 3v4M16 3v4M12 13.5v4M10 15.5h4" />
            </svg>
            탭해서 기한 지정
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { ArrowRight } from '@element-plus/icons-vue'
import { getProfileImageUrl, getProfileInitial } from '@/utils/profileUtils'

/**
 * 기한 없는 업무 레일.
 *
 * 모바일에서는 44px 한 줄로 접어 둔다. 844px 화면에서 캘린더 그리드가
 * 가져가야 할 세로 공간을 상시로 110px 씩 뺏기지 않게 하려는 것이고,
 * 접힘 상태는 기기별로 기억한다.
 */
const LS_KEY = 'agenda_backlog_open'

export default {
  name: 'BacklogRail',
  components: { ArrowRight },
  props: {
    items: { type: Array, default: () => [] }
  },
  emits: ['item-click'],
  setup() {
    const open = ref(false)

    onMounted(() => {
      try { open.value = localStorage.getItem(LS_KEY) === '1' } catch { /* 무시 */ }
    })

    function toggle() {
      open.value = !open.value
      try { localStorage.setItem(LS_KEY, open.value ? '1' : '0') } catch { /* 무시 */ }
    }

    return {
      open,
      toggle,
      avatarOf: (u) => getProfileImageUrl(u),
      initialOf: (u) => getProfileInitial(u)
    }
  }
}
</script>

<style scoped>
.brail {
  border-top: 1px solid var(--color-border);
  background: var(--color-surface);
  flex-shrink: 0;
  padding-bottom: var(--safe-bottom);
}

.brail__head {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  /* 접기/펼치기는 탭 대상이다. 40px 이었던 것을 44 로 올린다. */
  min-height: var(--tap-min);
  padding: 0 var(--sp-4);
  border: 0;
  background: none;
  cursor: pointer;
  color: var(--color-text-muted);
  font-family: inherit;
  font-size: var(--fs-sm);
}
.brail__head:hover { color: var(--color-text); }
.brail__head:active { background: var(--color-surface-2); }

.brail__caret {
  font-size: var(--fs-sm);
  transition: transform var(--dur-fast) var(--ease);
}
.is-open .brail__caret { transform: rotate(90deg); }

.brail__label { font-weight: var(--fw-semibold); }

/*
 * 가로 레일.
 *
 * 예전에는 flex-wrap 으로 칩을 세로로 흘려보내고 max-height:40vh 를 줬다.
 * 항목이 열 개만 넘어도 캘린더 그리드의 절반을 덮었고, 접었다 펴는 것이
 * 곧 "그리드를 반쯤 가린다" 는 뜻이었다. 지금은 높이가 카드 한 장으로
 * 고정되고 넘치는 것은 가로로 흐른다 — 세로 공간이 이 화면에서 가장 비싸다.
 */
.brail__viewport { position: relative; }

/* 오른쪽 페이드. "더 있다" 를 스크롤바 없이 말한다. 카드 위에 얹히지만
   pointer-events 를 꺼서 마지막 카드의 탭을 가로채지 않는다. */
.brail__viewport::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 28px;
  pointer-events: none;
  background: linear-gradient(90deg, transparent, var(--color-surface));
}

.brail__strip {
  display: flex;
  align-items: stretch;
  gap: var(--sp-2);
  padding: 0 var(--sp-4) var(--sp-3);
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x proximity;
}
.brail__strip > * { scroll-snap-align: start; }

/* 손가락으로 미는 화면에서는 스크롤바를 감춘다(페이드가 대신 말한다).
   마우스에서는 남긴다 — 휠만으로는 가로 스크롤이 되지 않아서
   스크롤바가 유일한 조작 수단인 경우가 있다. */
@media (pointer: coarse) {
  .brail__strip { scrollbar-width: none; }
  .brail__strip::-webkit-scrollbar { display: none; }
}

.brail__card {
  flex: 0 0 216px;
  width: 216px;
  display: flex;
  flex-direction: column;
  padding: 0;
  border: 1px solid var(--color-border);
  border-radius: var(--r-lg);
  background: var(--color-surface);
  box-shadow: var(--sh-1);
  overflow: hidden;
  text-align: left;
  cursor: pointer;
  transition: box-shadow var(--dur) var(--ease), transform var(--dur) var(--ease), border-color var(--dur) var(--ease);
}
.brail__card:hover {
  box-shadow: var(--sh-2);
  transform: translateY(-1px);
  border-color: var(--color-border-strong);
}
.brail__card:active { background: var(--color-surface-2); }
.brail__card.is-done { background: var(--color-surface-3); }

.brail__prio { height: 3px; flex-shrink: 0; background: var(--color-border-strong); }
.p-high .brail__prio { background: var(--color-danger); }
.p-medium .brail__prio { background: var(--color-warning); }
.p-low .brail__prio { background: var(--color-success); }

.brail__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  padding: var(--sp-2) var(--sp-3);
}

.brail__title {
  font-size: var(--fs-md);
  font-weight: var(--fw-semibold);
  color: var(--color-text);
  line-height: 1.4;
  letter-spacing: -0.01em;
  /* 두 줄 고정. 카드마다 높이가 달라지면 레일 아래가 들쭉날쭉해진다. */
  min-height: calc(var(--fs-md) * 1.4 * 2);
}
.is-done .brail__title { color: var(--color-text-muted); text-decoration: line-through; }

.brail__foot {
  display: flex;
  align-items: center;
  gap: var(--sp-1);
  min-width: 0;
}

.brail__dot { width: 7px; height: 7px; border-radius: var(--r-full); flex-shrink: 0; }
.brail__status {
  font-size: var(--fs-xs);
  color: var(--color-text-muted);
  white-space: nowrap;
}

/* 담당자는 오른쪽 끝. 아바타는 줄어들면 원이 찌그러진다. */
.brail__av { margin-left: auto; flex-shrink: 0; border: 1px solid var(--color-border); box-shadow: none; font-size: var(--fs-2xs); }
.brail__unassigned {
  margin-left: auto;
  font-size: var(--fs-xs);
  color: var(--color-warning);
  font-weight: var(--fw-medium);
  white-space: nowrap;
}

.brail__act {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: var(--sp-1) var(--sp-2);
  border-top: 1px solid var(--color-border);
  background: var(--color-surface-3);
  font-size: var(--fs-xs);
  color: var(--color-text-subtle);
  white-space: nowrap;
}
.brail__card:hover .brail__act { color: var(--color-primary); }

@media (max-width: 767px) { /* --bp-mobile-max */
  .brail__head { padding: 0 var(--sp-3); }
  .brail__strip { padding: 0 var(--sp-3) var(--sp-3); }
  /* 좁은 화면에서는 두 번째 카드가 살짝 보이게 폭을 줄인다 — 가로로
     더 있다는 신호를 페이드와 함께 두 번 준다. */
  .brail__card { flex-basis: 200px; width: 200px; }
}
</style>
